import { useMemo, useState } from "react";
import { Input } from "../ui/input";
import { Search, User, Users, Mail, Phone, Receipt, DollarSign, Trash2, ShieldCheck } from "lucide-react";

export default function CustomersTab({ customers, onSelectCustomer, onPromoteCustomer, onDeleteCustomer, isSuperAdmin }) {
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("last_order");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q
      ? customers.filter(c =>
          c.email?.toLowerCase().includes(q) ||
          c.name?.toLowerCase().includes(q) ||
          c.phone?.includes(q)
        )
      : [...customers];

    if (sortBy === "revenue") list.sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0));
    else if (sortBy === "orders") list.sort((a, b) => (b.total_orders || 0) - (a.total_orders || 0));
    else if (sortBy === "name") list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else list.sort((a, b) => (b.last_order_date || "").localeCompare(a.last_order_date || ""));

    return list;
  }, [customers, query, sortBy]);

  const totals = useMemo(() => ({
    count: customers.length,
    revenue: customers.reduce((s, c) => s + (c.total_revenue || 0), 0),
    orders: customers.reduce((s, c) => s + (c.total_orders || 0), 0)
  }), [customers]);

  return (
    <div data-testid="customers-tab">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-[#C5A059]" /> Customers ({totals.count})
        </h2>
        <div className="flex items-center gap-4 text-xs">
          <span className="text-white/40">Total Orders: <span className="text-white font-semibold">{totals.orders}</span></span>
          <span className="text-white/40">Total Revenue: <span className="text-[#C5A059] font-semibold">{"\u20AC"}{totals.revenue.toFixed(2)}</span></span>
        </div>
      </div>

      <div className="bg-[#0E0E0E] border border-white/5 rounded-sm p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input placeholder="Search by name, email, phone..." value={query} onChange={(e) => setQuery(e.target.value)}
            className="input-dark pl-10" data-testid="customer-search" />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/40">Sort:</span>
          {[
            { v: "last_order", l: "Last Order" },
            { v: "revenue", l: "Revenue" },
            { v: "orders", l: "Orders" },
            { v: "name", l: "Name" }
          ].map(s => (
            <button key={s.v} onClick={() => setSortBy(s.v)}
              className={`px-3 py-1 rounded-sm border transition-colors ${sortBy === s.v ? "bg-[#C5A059]/20 text-[#C5A059] border-[#C5A059]/30" : "bg-white/5 text-white/50 border-white/10 hover:text-white"}`}
              data-testid={`sort-${s.v}`}>
              {s.l}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12"><Users className="w-12 h-12 text-white/20 mx-auto mb-4" /><p className="text-white/50">No customers found</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="customers-table">
            <thead>
              <tr><th>Customer</th><th>Contact</th><th>Orders</th><th>Revenue</th><th>Last Order</th><th>Account</th>{isSuperAdmin && <th>Actions</th>}</tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.email} className="hover:bg-white/5 transition-colors" data-testid={`customer-row-${c.email}`}>
                  <td onClick={() => onSelectCustomer(c.email)} className="cursor-pointer">
                    <div className="flex items-center gap-3">
                      {c.picture ? (
                        <img src={c.picture} alt={c.name} className="w-9 h-9 rounded-full" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                          <User className="w-4 h-4 text-[#C5A059]" />
                        </div>
                      )}
                      <div>
                        <div className="text-white text-sm font-medium">{c.name || "(no name)"}</div>
                        <div className="text-white/30 text-[10px]">Click for details &rarr;</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="text-white/70 text-xs flex items-center gap-1"><Mail className="w-3 h-3 text-white/30" /> {c.email}</div>
                    {c.phone && <div className="text-white/40 text-xs flex items-center gap-1"><Phone className="w-3 h-3 text-white/30" /> {c.phone}</div>}
                  </td>
                  <td>
                    <div className="flex items-center gap-1 text-white text-sm"><Receipt className="w-3 h-3 text-white/30" /> {c.total_orders}</div>
                    {c.pending_orders > 0 && <div className="text-amber-400 text-[10px]">{c.pending_orders} pending</div>}
                    {c.confirmed_orders > 0 && <div className="text-emerald-400 text-[10px]">{c.confirmed_orders} confirmed</div>}
                  </td>
                  <td>
                    <div className="text-[#C5A059] font-semibold font-mono text-sm flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />{c.total_revenue.toFixed(2)}
                    </div>
                  </td>
                  <td className="text-white/50 font-mono text-xs">
                    {c.last_order_date ? c.last_order_date.split("T")[0] : "-"}
                  </td>
                  <td>
                    {c.has_account ? (
                      <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">Registered</span>
                    ) : (
                      <span className="text-[10px] bg-white/10 text-white/40 px-2 py-0.5 rounded">Guest</span>
                    )}
                  </td>
                  {isSuperAdmin && (
                    <td>
                      <div className="flex items-center gap-1">
                        {c.has_account && (
                          <PromoteMenu customer={c} onPromote={onPromoteCustomer} />
                        )}
                        {c.has_account && c.user_id && onDeleteCustomer && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onDeleteCustomer(c); }}
                            className="p-1.5 text-white/40 hover:text-red-400 transition-colors"
                            title="Delete customer (irreversible)"
                            data-testid={`delete-customer-${c.email}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PromoteMenu({ customer, onPromote }) {
  const [open, setOpen] = useState(false);
  if (!onPromote) return null;
  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="p-1.5 text-white/40 hover:text-emerald-400 transition-colors"
        title="Promote to staff/driver"
        data-testid={`promote-${customer.email}`}
      >
        <ShieldCheck className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div
            className="absolute right-0 mt-1 w-44 bg-[#151515] border border-white/10 rounded-sm shadow-lg z-50 py-1 text-xs"
            data-testid={`promote-menu-${customer.email}`}
          >
            <p className="px-3 py-1 text-white/30 uppercase text-[10px] tracking-wider">Promote to&hellip;</p>
            {["admin", "super_admin", "driver"].map((r) => (
              <button
                key={r}
                onClick={(e) => { e.stopPropagation(); setOpen(false); onPromote(customer, r); }}
                className="block w-full text-left px-3 py-2 text-white/80 hover:bg-white/5 hover:text-[#C5A059] capitalize"
                data-testid={`promote-${r}-${customer.email}`}
              >
                {r === "super_admin" ? "Super Admin" : r}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
